import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import type { ElementRef, OnDestroy, AfterViewInit } from '@angular/core'
import { Component, DestroyRef, effect, inject, input, linkedSignal, untracked, output, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import type { Subscription } from 'rxjs'
import { fromEvent, debounceTime, distinctUntilChanged, map, filter, tap } from 'rxjs'
import { AutoUnsubscribe } from '@awork/_shared/decorators/auto-unsubscribe'

export type SearchInputVariant = 'default' | 'filled'

@AutoUnsubscribe()
@Component({
  selector: 'aw-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  imports: [IconsDirective]
})
export class SearchInputComponent implements AfterViewInit, OnDestroy {
  private destroyRef = inject(DestroyRef)
  protected readonly commonTranslations = q.translations.common

  readonly placeholder = input(`Search...`)
  readonly minLength = input(2)
  readonly showButtonAlways = input(false)
  readonly autofocus = input(false)
  readonly currentValue = input('')
  readonly variant = input<SearchInputVariant>('default')

  readonly onSearch = output<string>()
  readonly enter = output<void>()
  readonly focused = output<void>()
  readonly blurred = output<void>()

  readonly searchField = viewChild<ElementRef<HTMLInputElement>>('searchField')

  readonly currentValueState = linkedSignal(() => this.currentValue())

  protected inputSubscription: Subscription
  private autofocusTimeoutId: number | null = null

  constructor() {
    effect(() => {
      const value = this.currentValue()
      untracked(() => this.updateCurrentValue(value, true))
    })
  }

  ngAfterViewInit(): void {
    this.autofocusTimeoutId = window.setTimeout(() => {
      if (this.autofocus()) {
        this.focus()
      }
    })

    const searchField = this.searchField()

    if (searchField?.nativeElement) {
      searchField.nativeElement.value = this.currentValueState()
    }

    this.setSearchEvent()
  }

  ngOnDestroy(): void {
    if (this.autofocusTimeoutId !== null) {
      clearTimeout(this.autofocusTimeoutId)
      this.autofocusTimeoutId = null
    }
  }

  /**
   * Emits the onSearch event when the user stops tipping (500ms),
   * the input is at least minLength long
   * and the input is different than the last one
   */
  private setSearchEvent(): void {
    const searchField = this.searchField()

    if (!searchField) {
      return
    }

    this.inputSubscription = fromEvent(searchField.nativeElement, 'keyup')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(event => {
          const target = event.target as HTMLInputElement
          return target.value.trim()
        }),
        tap(value => {
          this.currentValueState.set(value)
        }),
        debounceTime(500),
        filter(value => {
          return value.length >= this.minLength() || value === ''
        }),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.onSearch.emit(value)
        this.currentValueState.set(value)
      })
  }

  /**
   * Updates the input state and optionally emits the normalized search value.
   * @param {string} value The incoming search value.
   * @param {boolean} emitSearch Whether the search event should be emitted.
   */
  private updateCurrentValue(value: string, emitSearch: boolean): void {
    const currentValue = (value || '').trim()

    this.currentValueState.set(currentValue)

    const searchField = this.searchField()

    if (searchField?.nativeElement) {
      searchField.nativeElement.value = currentValue
    }

    if (emitSearch && currentValue) {
      this.onSearch.emit(currentValue)
    }
  }

  /**
   * Clears the field and emits the onSearch event
   */
  clear(): void {
    const oldVal = this.currentValueState()

    this.currentValueState.set('')

    const searchField = this.searchField()

    searchField.nativeElement.value = ''
    searchField.nativeElement.dispatchEvent(new KeyboardEvent('keyup', { code: 'Delete' }))

    // keep focus, when clearing the field which had a value
    if (oldVal && oldVal.trim() !== '') {
      searchField.nativeElement.focus({ preventScroll: true })
    }
  }

  /**
   * Focuses the search input
   */
  focus(): void {
    const searchField = this.searchField()

    if (searchField && searchField.nativeElement) {
      searchField.nativeElement.focus()
    }
  }

  /**
   * Blurs the search input
   */
  blur(): void {
    const searchField = this.searchField()

    if (searchField && searchField.nativeElement) {
      searchField.nativeElement.blur()
    }
  }
}
